import {
  Badge,
  Box,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Progress,
  SimpleGrid,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  FiArrowUpRight,
  FiCheckCircle,
  FiClock,
  FiMail,
  FiPhone,
  FiTarget,
  FiUsers,
  FiXCircle,
} from 'react-icons/fi';
import { AiOutlineUsergroupAdd } from 'react-icons/ai';
import { IoLogoWhatsapp, IoWarningOutline } from 'react-icons/io5';
import { MdHowToReg, MdOutlineChecklist } from 'react-icons/md';
import { RiMapPin2Line, RiSuitcaseLine, RiTeamLine } from 'react-icons/ri';
import { SiMicrosoftteams } from 'react-icons/si';
import HeaderSideBar from '../components/HeaderSideBar';
import { PermissionByIdDTO, VoterDTO } from '../dtos';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface DashboardMetricItem {
  label: string;
  value: number;
}

interface MonthlyEvolutionItem {
  date: string;
  votersTotal: number;
  tasksTotal: number;
  votersCreated: number;
  tasksCreated: number;
  tasksCompleted: number;
}

interface DashboardOverview {
  totals: {
    role: number;
    task: number;
    user: number;
    voter: number;
  };
  taskStatus: {
    backlog: number;
    doing: number;
    completed: number;
    overdue: number;
  };
  taskPriority: {
    high: number;
    medium: number;
    low: number;
  };
  engagement: {
    votersWithPhone: number;
    votersWithEmail: number;
    votersWithTask: number;
    newVotersLast30Days: number;
    tasksCreatedLast30Days: number;
    tasksCompletedLast30Days: number;
  };
  birthdays: {
    today: number;
    upcomingWeek: number;
  };
  topCities: DashboardMetricItem[];
  monthlyEvolution: MonthlyEvolutionItem[];
}

const emptyOverview: DashboardOverview = {
  totals: {
    role: 0,
    task: 0,
    user: 0,
    voter: 0,
  },
  taskStatus: {
    backlog: 0,
    doing: 0,
    completed: 0,
    overdue: 0,
  },
  taskPriority: {
    high: 0,
    medium: 0,
    low: 0,
  },
  engagement: {
    votersWithPhone: 0,
    votersWithEmail: 0,
    votersWithTask: 0,
    newVotersLast30Days: 0,
    tasksCreatedLast30Days: 0,
    tasksCompletedLast30Days: 0,
  },
  birthdays: {
    today: 0,
    upcomingWeek: 0,
  },
  topCities: [],
  monthlyEvolution: [],
};

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value)}%`;
}

export default function Home() {
  const { office } = useAuth();
  const [overview, setOverview] = useState<DashboardOverview>(emptyOverview);
  const [permissionBirthDates, setPermissionBirthDates] = useState<
    PermissionByIdDTO[]
  >([]);
  const [voterBirthDates, setVoterBirthDates] = useState<VoterDTO[]>([]);
  const [birthdayStatusByVoterId, setBirthdayStatusByVoterId] = useState<
    Record<string, string>
  >({});
  const [birthdayStatusEnabled, setBirthdayStatusEnabled] = useState(false);
  const [birthdayMessageTemplate, setBirthdayMessageTemplate] =
    useState<string>('');

  const defaultBirthdayTemplate = useMemo(() => {
    const ownerPosition = office?.owner_position
      ? ` ${office.owner_position}`
      : '';
    const officeName = office?.name ? ` ${office.name}` : '';

    return `Olá, {{nome}}! Aqui é do Gabinete do${ownerPosition}${officeName}. Gostaria de desejar um feliz aniversário!`;
  }, [office?.name, office?.owner_position]);

  const birthdayTemplateStorageKey = useMemo(() => {
    return office?.id ? `texto-aniversario-${office.id}` : '';
  }, [office?.id]);

  const completionRate = useMemo(() => {
    const totalTasks = overview.totals.task || 0;
    return totalTasks > 0
      ? (overview.taskStatus.completed / totalTasks) * 100
      : 0;
  }, [overview.taskStatus.completed, overview.totals.task]);

  const overdueRate = useMemo(() => {
    const totalTasks = overview.totals.task || 0;
    return totalTasks > 0 ? (overview.taskStatus.overdue / totalTasks) * 100 : 0;
  }, [overview.taskStatus.overdue, overview.totals.task]);

  const phoneCoverage = useMemo(() => {
    const totalVoters = overview.totals.voter || 0;
    return totalVoters > 0
      ? (overview.engagement.votersWithPhone / totalVoters) * 100
      : 0;
  }, [overview.engagement.votersWithPhone, overview.totals.voter]);

  const engagementRate = useMemo(() => {
    const totalVoters = overview.totals.voter || 0;
    return totalVoters > 0
      ? (overview.engagement.votersWithTask / totalVoters) * 100
      : 0;
  }, [overview.engagement.votersWithTask, overview.totals.voter]);

  const monthlyEvolution = useMemo(() => {
    return overview.monthlyEvolution.map(item => ({
      ...item,
      monthLabel: new Date(item.date).toLocaleDateString('pt-BR', {
        month: 'short',
      }),
    }));
  }, [overview.monthlyEvolution]);

  const taskStatusData = useMemo(
    () => [
      {
        label: 'Backlog',
        value: overview.taskStatus.backlog,
        color: '#94A3B8',
      },
      {
        label: 'Em andamento',
        value: overview.taskStatus.doing,
        color: office?.primary_color || '#0F766E',
      },
      {
        label: 'Concluidas',
        value: overview.taskStatus.completed,
        color: '#16A34A',
      },
      {
        label: 'Atrasadas',
        value: overview.taskStatus.overdue,
        color: '#DC2626',
      },
    ],
    [office?.primary_color, overview.taskStatus],
  );

  const taskPriorityData = useMemo(
    () => [
      {
        name: 'Alta',
        value: overview.taskPriority.high,
        color: '#DC2626',
      },
      {
        name: 'Media',
        value: overview.taskPriority.medium,
        color: '#F59E0B',
      },
      {
        name: 'Baixa',
        value: overview.taskPriority.low,
        color: '#2563EB',
      },
    ].filter(item => item.value > 0),
    [overview.taskPriority],
  );

  const heroMetrics = useMemo(
    () => [
      {
        label: 'Cargos',
        value: overview.totals.role,
        icon: RiSuitcaseLine,
      },
      {
        label: 'Equipe',
        value: overview.totals.user,
        icon: AiOutlineUsergroupAdd,
      },
      {
        label: 'Apoiadores',
        value: overview.totals.voter,
        icon: MdHowToReg,
      },
      {
        label: 'Demandas',
        value: overview.totals.task,
        icon: MdOutlineChecklist,
      },
    ],
    [overview.totals],
  );

  const highlightCards = useMemo(
    () => [
      {
        title: 'Conclusao operacional',
        value: formatPercent(completionRate),
        helper: `${overview.taskStatus.completed} demandas finalizadas`,
        icon: FiTarget,
      },
      {
        title: 'Cobertura de celular',
        value: formatPercent(phoneCoverage),
        helper: `${overview.engagement.votersWithPhone} apoiadores com WhatsApp potencial`,
        icon: FiPhone,
      },
      {
        title: 'Base acionada',
        value: formatPercent(engagementRate),
        helper: `${overview.engagement.votersWithTask} apoiadores com alguma demanda`,
        icon: FiUsers,
      },
      {
        title: 'Risco de atraso',
        value: formatPercent(overdueRate),
        helper: `${overview.taskStatus.overdue} demandas vencidas`,
        icon: FiClock,
      },
    ],
    [
      completionRate,
      engagementRate,
      overview.engagement.votersWithPhone,
      overview.engagement.votersWithTask,
      overview.taskStatus.completed,
      overview.taskStatus.overdue,
      overdueRate,
      phoneCoverage,
    ],
  );

  const getOverview = useCallback(async () => {
    try {
      const response = await api.get(`/dashboard/overview/${office?.id}`);
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to load dashboard overview', error);
    }
  }, [office?.id]);

  const getBirthDates = useCallback(async () => {
    try {
      const [permissions, voters] = await Promise.all([
        api.get(`/permission/by-birthdate/${office?.id}`),
        api.get(`/voter/by-birthdate/${office?.id}`),
      ]);

      setPermissionBirthDates(permissions.data);
      setVoterBirthDates(voters.data);
    } catch (error) {
      console.error('Failed to load birthday data', error);
    }
  }, [office?.id]);

  function formatWeekdayToBrazilian(dateString: string) {
    const birthDate = new Date(dateString);
    const currentYear = new Date().getFullYear();

    const thisYearBirthDate = new Date(
      currentYear,
      birthDate.getMonth(),
      birthDate.getDate(),
    );

    return thisYearBirthDate.toLocaleDateString('pt-BR', {
      weekday: 'long',
    });
  }

  function isSameDayAsToday(dateString: string) {
    const birthDate = new Date(dateString);
    const today = new Date();

    return (
      birthDate.getDate() === today.getDate() &&
      birthDate.getMonth() === today.getMonth()
    );
  }

  function normalizePhone(phone?: string | null) {
    return (phone ?? '').replace(/\D/g, '');
  }

  function buildBirthdayMessage(personName: string) {
    const template = birthdayMessageTemplate?.trim()
      ? birthdayMessageTemplate
      : defaultBirthdayTemplate;

    return template.replace(/\{\{nome\}\}/g, personName);
  }

  function buildWhatsAppLink(
    phone?: string | null,
    personName?: string | null,
  ) {
    const cleaned = normalizePhone(phone);
    if (!cleaned) return '';

    const message = buildBirthdayMessage((personName ?? '').trim());
    return `https://wa.me/55${cleaned}?text=${encodeURIComponent(message)}`;
  }

  const getBirthdayStatus = useCallback(async () => {
    if (!office?.birthday_auto_enabled) {
      setBirthdayStatusEnabled(false);
      setBirthdayStatusByVoterId({});
      return;
    }

    try {
      const response = await api.get('/admin/birthday-message/status-today', {
        params: { office_id: office?.id },
      });

      setBirthdayStatusEnabled(Boolean(response.data?.enabled));
      setBirthdayStatusByVoterId(response.data?.statusByVoterId || {});
    } catch (error) {
      console.error('Failed to load birthday status', error);
      setBirthdayStatusEnabled(false);
      setBirthdayStatusByVoterId({});
    }
  }, [office?.birthday_auto_enabled, office?.id]);

  function getBirthdayStatusIcon(voterId: string) {
    const status = birthdayStatusByVoterId[voterId] || 'PENDING';

    if (status === 'SENT') {
      return (
        <Icon as={FiCheckCircle} color="green.500" fontSize="16px" ml="6px" />
      );
    }

    if (status === 'FAILED') {
      return <Icon as={FiXCircle} color="red.500" fontSize="16px" ml="6px" />;
    }

    return <Icon as={FiClock} color="orange.400" fontSize="16px" ml="6px" />;
  }

  useEffect(() => {
    if (!office?.id) return;

    getOverview();
    getBirthDates();
    getBirthdayStatus();
  }, [
    getBirthDates,
    getBirthdayStatus,
    getOverview,
    office?.birthday_auto_enabled,
    office?.id,
  ]);

  useEffect(() => {
    if (!office?.id || !birthdayTemplateStorageKey) return;

    try {
      const saved = localStorage.getItem(birthdayTemplateStorageKey);

      if (saved?.trim()) {
        setBirthdayMessageTemplate(saved);
        return;
      }

      setBirthdayMessageTemplate(defaultBirthdayTemplate);
    } catch (error) {
      setBirthdayMessageTemplate(defaultBirthdayTemplate);
    }
  }, [birthdayTemplateStorageKey, defaultBirthdayTemplate, office?.id]);

  useEffect(() => {
    if (!office?.id || !birthdayTemplateStorageKey) return;

    try {
      if (birthdayMessageTemplate?.trim()) {
        localStorage.setItem(
          birthdayTemplateStorageKey,
          birthdayMessageTemplate,
        );
      }
    } catch (error) {}
  }, [birthdayMessageTemplate, birthdayTemplateStorageKey, office?.id]);

  return (
    <HeaderSideBar>
      <Flex direction="column" gap="16px" pb="24px">
        <Box
          position="relative"
          overflow="hidden"
          borderRadius="24px"
          px={{ base: '20px', md: '28px' }}
          py={{ base: '22px', md: '28px' }}
          bgGradient={`linear(135deg, ${office?.primary_color || '#0F766E'} 0%, ${
            office?.secondary_color || '#0B1324'
          } 100%)`}
          color="white"
          boxShadow="0 24px 80px rgba(15, 23, 42, 0.22)"
        >
          <Box
            position="absolute"
            top="-20px"
            right="-40px"
            width="220px"
            height="220px"
            borderRadius="full"
            bg="rgba(255,255,255,0.08)"
          />
          <Box
            position="absolute"
            bottom="-70px"
            left="-30px"
            width="180px"
            height="180px"
            borderRadius="full"
            bg="rgba(255,255,255,0.06)"
          />

          <Flex
            position="relative"
            zIndex={1}
            direction={{ base: 'column', lg: 'row' }}
            justify="space-between"
            gap="16px"
          >
            <Box maxW="640px">
              <Badge
                bg="rgba(255,255,255,0.16)"
                color="white"
                borderRadius="full"
                px="12px"
                py="4px"
                mb="12px"
              >
                Dashboard estrategico
              </Badge>
              <Text fontSize={{ base: '24px', md: '34px' }} fontWeight="700">
                Panorama do gabinete {office?.name ? ` ${office.name}` : ''}
              </Text>
              <Text
                mt="8px"
                maxW="560px"
                color="rgba(255,255,255,0.82)"
                fontSize={{ base: '14px', md: '16px' }}
              >
                Indicadores de base, produtividade, atrasos e capilaridade para
                orientar priorizacao, mobilizacao e acompanhamento da equipe.
              </Text>
            </Box>

            <SimpleGrid
              columns={{ base: 2, md: 4 }}
              spacing="12px"
              minW={{ base: '100%', lg: '420px' }}
            >
              {heroMetrics.map(metric => (
                <Box
                  key={metric.label}
                  p="16px"
                  borderRadius="18px"
                  bg="rgba(255,255,255,0.12)"
                  border="1px solid rgba(255,255,255,0.12)"
                  backdropFilter="blur(8px)"
                >
                  <Icon as={metric.icon} fontSize="22px" mb="10px" />
                  <Text fontSize="12px" color="rgba(255,255,255,0.72)">
                    {metric.label}
                  </Text>
                  <Text fontSize={{ base: '24px', md: '30px' }} fontWeight="700">
                    {metric.value}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="16px">
          {highlightCards.map(card => (
            <Box
              key={card.title}
              bg="white"
              borderRadius="20px"
              p="18px"
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              border="1px solid #E2E8F0"
            >
              <Flex justify="space-between" align="center" mb="16px">
                <Text fontSize="13px" color="#64748B" fontWeight="600">
                  {card.title}
                </Text>
                <Icon as={card.icon} fontSize="18px" color="#0F766E" />
              </Flex>
              <Text fontSize="36px" lineHeight="1" fontWeight="700" color="#0F172A">
                {card.value}
              </Text>
              <Text mt="8px" fontSize="13px" color="#64748B">
                {card.helper}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Grid
          templateColumns={{ base: '1fr', xl: '1.7fr 1fr' }}
          gap="16px"
          alignItems="stretch"
        >
          <GridItem>
            <Box
              bg="white"
              borderRadius="24px"
              p={{ base: '18px', md: '22px' }}
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              border="1px solid #E2E8F0"
              h="100%"
            >
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                gap="10px"
                mb="20px"
              >
                <Box>
                  <Text fontSize="20px" fontWeight="700" color="#0F172A">
                    Evolucao da operacao
                  </Text>
                  <Text fontSize="13px" color="#64748B">
                    Comparativo mensal entre base cadastrada e fluxo de demandas.
                  </Text>
                </Box>
                <Badge
                  alignSelf="flex-start"
                  borderRadius="full"
                  px="12px"
                  py="6px"
                  bg="#ECFDF5"
                  color="#047857"
                >
                  Ultimos 6 meses
                </Badge>
              </Flex>

              <Box h={{ base: '320px', md: '380px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthlyEvolution}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="monthLabel" tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                    <Tooltip />
                    <Bar
                      dataKey="votersCreated"
                      name="Novos apoiadores"
                      radius={[8, 8, 0, 0]}
                      fill={office?.primary_color || '#0F766E'}
                    />
                    <Bar
                      dataKey="tasksCreated"
                      name="Demandas abertas"
                      radius={[8, 8, 0, 0]}
                      fill="#CBD5E1"
                    />
                    <Line
                      type="monotone"
                      dataKey="tasksCompleted"
                      name="Demandas concluidas"
                      stroke="#16A34A"
                      strokeWidth={3}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              borderRadius="24px"
              p={{ base: '18px', md: '22px' }}
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              border="1px solid #E2E8F0"
              h="100%"
            >
              <Text fontSize="20px" fontWeight="700" color="#0F172A">
                Saude do funil
              </Text>
              <Text fontSize="13px" color="#64748B" mb="18px">
                Leitura rapida da capacidade de resposta e acionamento da base.
              </Text>

              <Flex direction="column" gap="18px">
                <Box>
                  <Flex justify="space-between" mb="6px">
                    <Text fontSize="13px" color="#475569" fontWeight="600">
                      Cobertura de e-mail
                    </Text>
                    <Text fontSize="13px" color="#0F172A" fontWeight="700">
                      {formatPercent(
                        overview.totals.voter > 0
                          ? (overview.engagement.votersWithEmail /
                              overview.totals.voter) *
                              100
                          : 0,
                      )}
                    </Text>
                  </Flex>
                  <Progress
                    value={
                      overview.totals.voter > 0
                        ? (overview.engagement.votersWithEmail /
                            overview.totals.voter) *
                          100
                        : 0
                    }
                    colorScheme="green"
                    borderRadius="full"
                    bg="#E2E8F0"
                  />
                </Box>

                <Box>
                  <Flex justify="space-between" mb="6px">
                    <Text fontSize="13px" color="#475569" fontWeight="600">
                      WhatsApp potencial
                    </Text>
                    <Text fontSize="13px" color="#0F172A" fontWeight="700">
                      {formatPercent(phoneCoverage)}
                    </Text>
                  </Flex>
                  <Progress
                    value={phoneCoverage}
                    colorScheme="teal"
                    borderRadius="full"
                    bg="#E2E8F0"
                  />
                </Box>

                <Box>
                  <Flex justify="space-between" mb="6px">
                    <Text fontSize="13px" color="#475569" fontWeight="600">
                      Base com historico de demanda
                    </Text>
                    <Text fontSize="13px" color="#0F172A" fontWeight="700">
                      {formatPercent(engagementRate)}
                    </Text>
                  </Flex>
                  <Progress
                    value={engagementRate}
                    colorScheme="blue"
                    borderRadius="full"
                    bg="#E2E8F0"
                  />
                </Box>

                <Divider />

                <SimpleGrid columns={2} spacing="12px">
                  <Box p="14px" borderRadius="18px" bg="#F8FAFC">
                    <Text fontSize="12px" color="#64748B">
                      Novos apoiadores
                    </Text>
                    <Text fontSize="28px" fontWeight="700" color="#0F172A">
                      {overview.engagement.newVotersLast30Days}
                    </Text>
                    <Text fontSize="12px" color="#64748B">
                      Ultimos 30 dias
                    </Text>
                  </Box>
                  <Box p="14px" borderRadius="18px" bg="#F8FAFC">
                    <Text fontSize="12px" color="#64748B">
                      Demandas concluidas
                    </Text>
                    <Text fontSize="28px" fontWeight="700" color="#0F172A">
                      {overview.engagement.tasksCompletedLast30Days}
                    </Text>
                    <Text fontSize="12px" color="#64748B">
                      Ultimos 30 dias
                    </Text>
                  </Box>
                </SimpleGrid>

                <Box
                  p="16px"
                  borderRadius="18px"
                  bg="#0F172A"
                  color="white"
                >
                  <Flex align="center" gap="10px" mb="6px">
                    <Icon as={FiArrowUpRight} />
                    <Text fontWeight="600">Leitura executiva</Text>
                  </Flex>
                  <Text fontSize="13px" color="rgba(255,255,255,0.76)">
                    {overview.engagement.tasksCompletedLast30Days >=
                    overview.engagement.tasksCreatedLast30Days
                      ? 'O time esta fechando demandas no mesmo ritmo ou acima da entrada recente.'
                      : 'A entrada recente de demandas esta acima da capacidade de conclusao e merece redistribuicao.'}
                  </Text>
                </Box>
              </Flex>
            </Box>
          </GridItem>
        </Grid>

        <Grid
          templateColumns={{ base: '1fr', xl: '1.15fr 0.85fr 1fr' }}
          gap="16px"
        >
          <GridItem>
            <Box
              bg="white"
              borderRadius="24px"
              p={{ base: '18px', md: '22px' }}
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              border="1px solid #E2E8F0"
              h="100%"
            >
              <Text fontSize="20px" fontWeight="700" color="#0F172A">
                Distribuicao das demandas
              </Text>
              <Text fontSize="13px" color="#64748B" mb="16px">
                Balanceamento atual entre backlog, execucao, entrega e atraso.
              </Text>

              <Box h="270px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={taskStatusData}
                    layout="vertical"
                    margin={{ top: 8, right: 12, left: 12, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={90}
                      tick={{ fill: '#475569', fontSize: 12 }}
                    />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                      {taskStatusData.map(item => (
                        <Cell key={item.label} fill={item.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <SimpleGrid columns={2} spacing="12px" mt="12px">
                {taskStatusData.map(item => (
                  <Box key={item.label} p="12px" borderRadius="16px" bg="#F8FAFC">
                    <Flex align="center" gap="8px">
                      <Box
                        width="10px"
                        height="10px"
                        borderRadius="full"
                        bg={item.color}
                      />
                      <Text fontSize="12px" color="#64748B">
                        {item.label}
                      </Text>
                    </Flex>
                    <Text mt="6px" fontSize="24px" fontWeight="700" color="#0F172A">
                      {item.value}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              borderRadius="24px"
              p={{ base: '18px', md: '22px' }}
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              border="1px solid #E2E8F0"
              h="100%"
            >
              <Text fontSize="20px" fontWeight="700" color="#0F172A">
                Prioridade das demandas
              </Text>
              <Text fontSize="13px" color="#64748B" mb="16px">
                Onde a urgencia esta concentrada agora.
              </Text>

              <Box h="270px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={taskPriorityData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={68}
                      outerRadius={92}
                      paddingAngle={4}
                    >
                      {taskPriorityData.map(item => (
                        <Cell key={item.name} fill={item.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              <Flex direction="column" gap="10px" mt="8px">
                {[
                  {
                    label: 'Alta',
                    value: overview.taskPriority.high,
                    color: '#DC2626',
                  },
                  {
                    label: 'Media',
                    value: overview.taskPriority.medium,
                    color: '#F59E0B',
                  },
                  {
                    label: 'Baixa',
                    value: overview.taskPriority.low,
                    color: '#2563EB',
                  },
                ].map(item => (
                  <Flex
                    key={item.label}
                    align="center"
                    justify="space-between"
                    p="12px"
                    borderRadius="14px"
                    bg="#F8FAFC"
                  >
                    <Flex align="center" gap="8px">
                      <Box
                        width="10px"
                        height="10px"
                        borderRadius="full"
                        bg={item.color}
                      />
                      <Text fontSize="13px" color="#475569">
                        {item.label}
                      </Text>
                    </Flex>
                    <Text fontSize="18px" fontWeight="700" color="#0F172A">
                      {item.value}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              borderRadius="24px"
              p={{ base: '18px', md: '22px' }}
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              border="1px solid #E2E8F0"
              h="100%"
            >
              <Flex align="center" justify="space-between" mb="12px">
                <Box>
                  <Text fontSize="20px" fontWeight="700" color="#0F172A">
                    Capilaridade territorial
                  </Text>
                  <Text fontSize="13px" color="#64748B">
                    Cidades com maior concentracao da base.
                  </Text>
                </Box>
                <Icon as={RiMapPin2Line} fontSize="20px" color="#0F766E" />
              </Flex>

              <Flex direction="column" gap="12px">
                {overview.topCities.length > 0 ? (
                  overview.topCities.map(city => {
                    const width =
                      overview.topCities[0]?.value > 0
                        ? (city.value / overview.topCities[0].value) * 100
                        : 0;

                    return (
                      <Box key={city.label}>
                        <Flex justify="space-between" mb="6px">
                          <Text fontSize="13px" color="#475569" fontWeight="600">
                            {city.label}
                          </Text>
                          <Text fontSize="13px" color="#0F172A" fontWeight="700">
                            {city.value}
                          </Text>
                        </Flex>
                        <Box
                          width="100%"
                          h="10px"
                          bg="#E2E8F0"
                          borderRadius="full"
                          overflow="hidden"
                        >
                          <Box
                            width={`${width}%`}
                            h="100%"
                            bg={office?.primary_color || '#0F766E'}
                            borderRadius="full"
                          />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box
                    p="16px"
                    borderRadius="18px"
                    bg="#F8FAFC"
                    color="#64748B"
                    fontSize="13px"
                  >
                    Ainda nao ha cidades suficientes cadastradas para gerar esse
                    recorte.
                  </Box>
                )}

                <Divider />

                <SimpleGrid columns={2} spacing="12px">
                  <Box p="14px" borderRadius="18px" bg="#F8FAFC">
                    <Flex align="center" gap="8px" mb="4px">
                      <Icon as={FiMail} color="#0F766E" />
                      <Text fontSize="12px" color="#64748B">
                        E-mails validos
                      </Text>
                    </Flex>
                    <Text fontSize="26px" fontWeight="700" color="#0F172A">
                      {overview.engagement.votersWithEmail}
                    </Text>
                  </Box>
                  <Box p="14px" borderRadius="18px" bg="#F8FAFC">
                    <Flex align="center" gap="8px" mb="4px">
                      <Icon as={FiPhone} color="#0F766E" />
                      <Text fontSize="12px" color="#64748B">
                        Celulares
                      </Text>
                    </Flex>
                    <Text fontSize="26px" fontWeight="700" color="#0F172A">
                      {overview.engagement.votersWithPhone}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Flex>
            </Box>
          </GridItem>
        </Grid>

        <Grid
          templateColumns={{ base: '1fr', xl: '1.15fr 0.85fr' }}
          gap="16px"
          alignItems="stretch"
        >
          <GridItem>
            <Box
              bg="white"
              borderRadius="24px"
              p={{ base: '18px', md: '22px' }}
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              border="1px solid #E2E8F0"
              h="100%"
            >
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                gap="12px"
                mb="16px"
              >
                <Box>
                  <Text fontSize="20px" fontWeight="700" color="#0F172A">
                    Aniversariantes e relacionamento
                  </Text>
                  <Text fontSize="13px" color="#64748B">
                    Mensagem padrao do WhatsApp com visibilidade dos contatos do
                    dia.
                  </Text>
                </Box>
                <Flex gap="8px" wrap="wrap">
                  <Badge borderRadius="full" px="12px" py="6px" bg="#EEF2FF" color="#4338CA">
                    Hoje: {overview.birthdays.today}
                  </Badge>
                  <Badge borderRadius="full" px="12px" py="6px" bg="#ECFDF5" color="#047857">
                    Prox. 7 dias: {overview.birthdays.upcomingWeek}
                  </Badge>
                </Flex>
              </Flex>

              <FormControl mb="16px">
                <FormLabel color="#64748B" fontSize="14px" mb="6px">
                  Mensagem do WhatsApp (use <b>{`{{nome}}`}</b> para o nome)
                </FormLabel>
                <Textarea
                  value={birthdayMessageTemplate}
                  onChange={e => setBirthdayMessageTemplate(e.target.value)}
                  placeholder={defaultBirthdayTemplate}
                  color="#0F172A"
                  fontSize="14px"
                  resize="vertical"
                  minH="110px"
                />
              </FormControl>

              <Flex direction="column" gap="12px">
                {permissionBirthDates.length === 0 &&
                  voterBirthDates.length === 0 && (
                    <Box
                      p="16px"
                      borderRadius="18px"
                      bg="#F8FAFC"
                      color="#64748B"
                      fontSize="14px"
                    >
                      Nenhum aniversariante hoje.
                    </Box>
                  )}

                {permissionBirthDates.map(permission => {
                  const waLink = buildWhatsAppLink(
                    permission?.user?.cellphone,
                    permission?.user?.name,
                  );

                  return (
                    <Flex
                      key={permission.id}
                      align="center"
                      justify="space-between"
                      p="14px"
                      borderRadius="18px"
                      bg="#F8FAFC"
                    >
                      <Flex align="center" gap="12px">
                        <Flex
                          width="42px"
                          height="42px"
                          borderRadius="14px"
                          bg="white"
                          align="center"
                          justify="center"
                        >
                          <Icon as={RiTeamLine} color="#475569" fontSize="22px" />
                        </Flex>
                        <Box>
                          <Text fontSize="15px" fontWeight="600" color="#0F172A">
                            {permission?.user?.name}
                          </Text>
                          <Text fontSize="12px" color="#64748B">
                            Equipe do gabinete
                          </Text>
                        </Box>
                      </Flex>

                      {waLink ? (
                        <a href={waLink} target="_blank" rel="noopener noreferrer">
                          <IconButton
                            aria-label="Enviar WhatsApp"
                            borderRadius="14px"
                            bg="white"
                            icon={
                              <Icon
                                as={IoLogoWhatsapp}
                                color="#16A34A"
                                fontSize="22px"
                              />
                            }
                          />
                        </a>
                      ) : (
                        <IconButton
                          aria-label="WhatsApp indisponivel"
                          borderRadius="14px"
                          bg="white"
                          isDisabled
                          icon={
                            <Icon
                              as={IoLogoWhatsapp}
                              color="#CBD5E1"
                              fontSize="22px"
                            />
                          }
                        />
                      )}
                    </Flex>
                  );
                })}

                {voterBirthDates
                  .sort((a, b) => {
                    const dateA = new Date(a.birthdate);
                    const dateB = new Date(b.birthdate);

                    if (dateA.getMonth() !== dateB.getMonth()) {
                      return dateA.getMonth() - dateB.getMonth();
                    }

                    return dateA.getDate() - dateB.getDate();
                  })
                  .map(voter => {
                    const waLink = buildWhatsAppLink(voter?.cellphone, voter?.name);

                    return (
                      <Flex
                        key={voter.id}
                        align="center"
                        justify="space-between"
                        p="14px"
                        borderRadius="18px"
                        bg="#F8FAFC"
                      >
                        <Flex align="center" gap="12px">
                          <Flex
                            width="42px"
                            height="42px"
                            borderRadius="14px"
                            bg="white"
                            align="center"
                            justify="center"
                          >
                            <Icon
                              as={SiMicrosoftteams}
                              color="#475569"
                              fontSize="20px"
                            />
                          </Flex>
                          <Box>
                            <Flex align="center">
                              <Text fontSize="15px" fontWeight="600" color="#0F172A">
                                {voter?.name}
                              </Text>
                              {birthdayStatusEnabled && getBirthdayStatusIcon(voter.id)}
                            </Flex>
                            <Flex align="center" gap="8px">
                              <Text
                                fontSize="12px"
                                color={
                                  isSameDayAsToday(voter?.birthdate)
                                    ? '#64748B'
                                    : 'orange.500'
                                }
                              >
                                {formatWeekdayToBrazilian(voter?.birthdate)}
                              </Text>
                              {!isSameDayAsToday(voter?.birthdate) && (
                                <Icon as={IoWarningOutline} color="orange.400" />
                              )}
                            </Flex>
                          </Box>
                        </Flex>

                        {waLink ? (
                          <a href={waLink} target="_blank" rel="noopener noreferrer">
                            <IconButton
                              aria-label="Enviar WhatsApp"
                              borderRadius="14px"
                              bg="white"
                              icon={
                                <Icon
                                  as={IoLogoWhatsapp}
                                  color="#16A34A"
                                  fontSize="22px"
                                />
                              }
                            />
                          </a>
                        ) : (
                          <IconButton
                            aria-label="WhatsApp indisponivel"
                            borderRadius="14px"
                            bg="white"
                            isDisabled
                            icon={
                              <Icon
                                as={IoLogoWhatsapp}
                                color="#CBD5E1"
                                fontSize="22px"
                              />
                            }
                          />
                        )}
                      </Flex>
                    );
                  })}
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              borderRadius="24px"
              p={{ base: '18px', md: '22px' }}
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              border="1px solid #E2E8F0"
              h="100%"
            >
              <Text fontSize="20px" fontWeight="700" color="#0F172A">
                Agenda executiva
              </Text>
              <Text fontSize="13px" color="#64748B" mb="18px">
                Leitura final para a rotina da equipe.
              </Text>

              <Flex direction="column" gap="12px">
                <Box p="16px" borderRadius="18px" bg="#F8FAFC">
                  <Text fontSize="12px" color="#64748B">
                    Demandas abertas x concluidas
                  </Text>
                  <Text fontSize="30px" fontWeight="700" color="#0F172A">
                    {overview.engagement.tasksCreatedLast30Days} /{' '}
                    {overview.engagement.tasksCompletedLast30Days}
                  </Text>
                  <Text fontSize="12px" color="#64748B">
                    entradas e entregas nos ultimos 30 dias
                  </Text>
                </Box>

                <Box p="16px" borderRadius="18px" bg="#F8FAFC">
                  <Text fontSize="12px" color="#64748B">
                    Apoiadores com historico de demanda
                  </Text>
                  <Text fontSize="30px" fontWeight="700" color="#0F172A">
                    {overview.engagement.votersWithTask}
                  </Text>
                  <Text fontSize="12px" color="#64748B">
                    base ja vinculada a algum atendimento
                  </Text>
                </Box>

                <Box p="16px" borderRadius="18px" bg="#F8FAFC">
                  <Text fontSize="12px" color="#64748B">
                    Demandas em execucao imediata
                  </Text>
                  <Text fontSize="30px" fontWeight="700" color="#0F172A">
                    {overview.taskStatus.doing + overview.taskStatus.overdue}
                  </Text>
                  <Text fontSize="12px" color="#64748B">
                    fazendo + atrasadas
                  </Text>
                </Box>

                <Box
                  p="18px"
                  borderRadius="18px"
                  bgGradient="linear(to-br, #FFF7ED, #FFEDD5)"
                >
                  <Text fontSize="12px" color="#9A3412" fontWeight="700">
                    Prioridade sugerida
                  </Text>
                  <Text mt="8px" fontSize="14px" color="#7C2D12">
                    {overview.taskStatus.overdue > 0
                      ? 'Atacar as demandas atrasadas primeiro e redistribuir carga da equipe.'
                      : 'O maior ganho agora esta em acelerar backlog e ampliar cadastro recente da base.'}
                  </Text>
                </Box>
              </Flex>
            </Box>
          </GridItem>
        </Grid>
      </Flex>
    </HeaderSideBar>
  );
}
